/**
 * @file phone.ts
 * @path packages/utils/src/formatters/phone.ts
 * @description
 */

/**
 * Formats a South African phone number for display with standard spacing.
 * It handles both international (+27) and local (0xx) formats.
 * @param input The raw phone number string to format.
 * @returns The formatted phone number, or the original input if it cannot be reliably formatted.
 */
export const formatPhone = (input: string): string => {
  if (!input) return "";

  const str = String(input);
  const cleaned = str.replace(/\D/g, "");

  if (
    str.startsWith("+27") ||
    (cleaned.startsWith("27") && cleaned.length === 11)
  ) {
    const mobilePart = cleaned.startsWith("27")
      ? cleaned.substring(2)
      : cleaned;
    const match = mobilePart.match(/^(\d{2})(\d{3})(\d{4})$/);
    if (match) {
      return `+27 ${match[1]} ${match[2]} ${match[3]}`;
    }
  }

  const matchLocal = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (matchLocal) {
    return `${matchLocal[1]} ${matchLocal[2]} ${matchLocal[3]}`;
  }

  return input;
};

/**
 * Masks a phone number for privacy, revealing only the last 4 digits.
 * @example formatMaskPhone("0821234567") // "*** *** 4567"
 * @param input The phone number string to mask.
 * @returns The masked phone number.
 */
export const formatMaskPhone = (input: string): string => {
  if (!input) return "";

  const cleaned = input.replace(/\D/g, "");
  if (cleaned.length < 4) return "***";

  const last4 = cleaned.slice(-4);
  return `*** *** ${last4}`;
};

/**
 * Converts a phone number into a `tel:` link format, ensuring it's in the international +27 format.
 * @param input The phone number string to convert.
 * @returns A string suitable for use in an `href` attribute (e.g., "tel:+27821234567").
 */
export const formatToTelLink = (input: string): string => {
  if (!input) return "";

  let cleaned = input.replace(/\D/g, "");

  if (cleaned.length === 10 && cleaned.startsWith("0")) {
    cleaned = "27" + cleaned.substring(1);
  }

  return `tel:+${cleaned}`;
};
